/**
 * captureFraming — pure crop-rect math for focused ability captures
 * (DJ ask 2026-06-12: "screen caps for a certain amount of pixels around
 * character center, and targets").
 *
 * Inputs are SCREEN-SPACE points (Pixi stage coords — convert world
 * positions via PixiOverlayHost.worldToScreen first). Modes:
 *  - 'viewport' → full frame (no crop)
 *  - 'player'   → sizePx × sizePx square centered on the caster
 *  - 'cast'     → the union of caster + target squares (so the projectile
 *                 path between them stays in frame); no target degrades to
 *                 'player'
 *
 * The rect is clamped INTO the screen by shifting first, shrinking only
 * when the requested size exceeds the screen itself — an edge-hugging
 * caster still gets a full-size crop biased inward.
 */
import type { CaptureFocusMode } from '../../config/diagnostics/abilityCapture.config';

export interface CaptureFrameRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function computeCaptureFrame(opts: {
  mode: CaptureFocusMode;
  sizePx: number;
  screenW: number;
  screenH: number;
  /** Caster sprite-center, screen-space. */
  player?: { x: number; y: number } | null;
  /** Target sprite-center, screen-space (cast mode only). */
  target?: { x: number; y: number } | null;
}): CaptureFrameRect {
  const full = { x: 0, y: 0, width: opts.screenW, height: opts.screenH };
  if (opts.mode === 'viewport' || !opts.player) return full;

  const half = Math.max(32, opts.sizePx) / 2;
  let minX = opts.player.x - half;
  let maxX = opts.player.x + half;
  let minY = opts.player.y - half;
  let maxY = opts.player.y + half;

  if (opts.mode === 'cast' && opts.target) {
    minX = Math.min(minX, opts.target.x - half);
    maxX = Math.max(maxX, opts.target.x + half);
    minY = Math.min(minY, opts.target.y - half);
    maxY = Math.max(maxY, opts.target.y + half);
  }

  const w = Math.min(maxX - minX, opts.screenW);
  const h = Math.min(maxY - minY, opts.screenH);
  const x = Math.min(Math.max(minX, 0), opts.screenW - w);
  const y = Math.min(Math.max(minY, 0), opts.screenH - h);
  return { x: Math.round(x), y: Math.round(y), width: Math.round(w), height: Math.round(h) };
}
